import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fileUrl, mlsUrl } from "@/lib/upload";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { AnnotationDialog } from "@/components/AnnotationDialog";
import { FeedbackPanel, StrokeOverlay } from "./FeedbackPanel";

/** Fullscreen photo viewer with prev/next navigation, original/MLS downloads,
 * and the editor-feedback/annotation panel. */
export function Lightbox({ job, images, file, onChange, onClose, onDownload }) {
  const idx = images.findIndex((f) => f.id === file.id);
  const go = (d) => onChange(images[(idx + d + images.length) % images.length]);
  const [annotating, setAnnotating] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const loadFeedback = useCallback(() => api.get(`/jobs/${job.id}/files/${file.id}/feedback`).then((r) => setFeedback(r.data)).catch(() => setFeedback([])), [job.id, file.id]);
  useEffect(() => { setAnnotating(false); loadFeedback(); }, [loadFeedback]);
  const send = async ({ comment, strokes }) => {
    try {
      await api.post(`/jobs/${job.id}/files/${file.id}/feedback`, { comment, strokes });
      toast.success("Feedback sent to the editing team");
      setAnnotating(false);
      loadFeedback();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not send feedback"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 bg-black border-0 rounded-none [&>button]:hidden" data-testid="lightbox">
        <DialogTitle className="sr-only">Media preview</DialogTitle>
        <DialogDescription className="sr-only">Preview this photo, download it when available, or leave feedback for the editing team.</DialogDescription>
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          {!job.downloads_locked && (
            <>
              <button data-testid="lightbox-download" onClick={() => onDownload(file)} title="Download original" className="h-10 px-3 bg-white/10 hover:bg-white/25 text-white flex items-center justify-center gap-2 backdrop-blur"><Download className="h-4 w-4" strokeWidth={1.5} /><span className="text-[10px] uppercase">Original</span></button>
              <a data-testid="lightbox-download-mls" href={mlsUrl(job.id, file.id)} title="Download MLS / Matrix size" className="h-10 px-3 bg-white/10 hover:bg-white/25 text-white flex items-center justify-center gap-2 backdrop-blur"><Download className="h-4 w-4" strokeWidth={1.5} /><span className="text-[10px] uppercase">MLS</span></a>
            </>
          )}
          <button data-testid="lightbox-close" onClick={onClose} className="h-10 w-10 bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"><X className="h-5 w-5" strokeWidth={1.5} /></button>
        </div>
        {images.length > 1 && (
          <div>
            <button data-testid="lightbox-prev" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"><ChevronLeft className="h-6 w-6" strokeWidth={1.5} /></button>
            <button data-testid="lightbox-next" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"><ChevronRight className="h-6 w-6" strokeWidth={1.5} /></button>
          </div>
        )}
        <div className="relative h-full w-full"><img src={fileUrl(job.id, file.id)} alt="" className="w-full h-full object-contain" /><StrokeOverlay feedback={feedback} /></div>
        <div data-testid="gallery-photo-ordinal-total" className="absolute bottom-3 left-4 text-white/70 text-xs font-mono">{file.original_filename} · {idx + 1}/{images.length}</div>
        <div className="absolute bottom-3 right-4 z-10 w-[min(360px,calc(100vw-2rem))] bg-white/95 backdrop-blur border border-white shadow-xl p-3"><FeedbackPanel feedback={feedback} onAnnotate={() => setAnnotating(true)} onSend={(comment) => send({ comment, strokes: [] })} /></div>
      </DialogContent>
      {annotating && <AnnotationDialog imageUrl={fileUrl(job.id, file.id)} onClose={() => setAnnotating(false)} onSave={send} />}
    </Dialog>
  );
}
